import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Snackbar } from '@mui/material';
import { useLocation } from 'react-router-dom';
import {
  clearDeferredPwaInstallPrompt,
  dismissPwaInstallPrompt,
  getDeferredPwaInstallPrompt,
  getPwaAppLabelForPath,
  isPwaAppInstalled,
  isPwaEntryPath,
  isPwaInstallDismissed,
  isStandalonePwa,
  PWA_INSTALL_AVAILABLE_EVENT,
  type BeforeInstallPromptEvent,
} from '../utils/pwaInstall';

const PwaInstallPrompt: React.FC = () => {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(() =>
      getDeferredPwaInstallPrompt(location.pathname),
    );
  const [open, setOpen] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [alreadyInstalled, setAlreadyInstalled] = useState(false);

  const syncPromptState = useCallback(() => {
    const prompt = getDeferredPwaInstallPrompt(location.pathname);
    const shouldShow =
      !!prompt &&
      isPwaEntryPath(location.pathname) &&
      !isStandalonePwa() &&
      !alreadyInstalled &&
      !isPwaInstallDismissed(location.pathname);

    setDeferredPrompt(prompt);
    setOpen(shouldShow);
  }, [alreadyInstalled, location.pathname]);

  const handleDismiss = useCallback(() => {
    dismissPwaInstallPrompt(location.pathname);
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;

    void isPwaAppInstalled(location.pathname).then((installed) => {
      if (!cancelled) {
        setAlreadyInstalled(installed);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    syncPromptState();

    const handleInstallAvailable = () => syncPromptState();
    window.addEventListener(PWA_INSTALL_AVAILABLE_EVENT, handleInstallAvailable);

    const intervalId = window.setInterval(syncPromptState, 2000);
    const stopPolling = window.setTimeout(() => {
      window.clearInterval(intervalId);
    }, 60000);

    return () => {
      window.removeEventListener(
        PWA_INSTALL_AVAILABLE_EVENT,
        handleInstallAvailable,
      );
      window.clearInterval(intervalId);
      window.clearTimeout(stopPolling);
    };
  }, [syncPromptState]);

  const handleInstall = useCallback(async () => {
    const prompt =
      deferredPrompt ?? getDeferredPwaInstallPrompt(location.pathname);
    if (!prompt) return;

    setInstalling(true);
    try {
      await prompt.prompt();
      await prompt.userChoice;
    } catch (error) {
      console.error('PWA install prompt failed:', error);
    } finally {
      setInstalling(false);
      clearDeferredPwaInstallPrompt(location.pathname);
      setDeferredPrompt(null);
      setOpen(false);
    }
  }, [deferredPrompt, location.pathname]);

  if (!open || !deferredPrompt) {
    return null;
  }

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ bottom: { xs: 16, sm: 24 } }}
      onClose={(_, reason) => {
        if (reason === 'clickaway') return;
        handleDismiss();
      }}
    >
      <Alert
        severity="info"
        variant="filled"
        sx={{
          width: '100%',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
        action={
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Button
              color="inherit"
              size="small"
              onClick={handleDismiss}
              disabled={installing}
            >
              Not now
            </Button>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={handleInstall}
              disabled={installing}
              sx={{ borderColor: 'rgba(255,255,255,0.7)' }}
            >
              Install now
            </Button>
          </Box>
        }
      >
        Install {getPwaAppLabelForPath(location.pathname)} on your device
      </Alert>
    </Snackbar>
  );
};

export default PwaInstallPrompt;
